import { useNavigate } from "@tanstack/react-router";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { useCan } from "@/shared/auth";
import { PageHeader } from "@/shared/components/PageHeader";
import { SectionCard } from "@/shared/components/SectionCard";
import { Button } from "@/shared/ui/button";

import { useAttentionCounts } from "../api/use-attention-counts";
import { useFulfillment } from "../api/use-fulfillment";
import { useNotifications } from "../api/use-notifications";
import { useSyncStatus } from "../api/use-sync-status";
import { KpiCard, Windows } from "../components/KpiCard";
import { LiveDot } from "../components/LiveDot";
import { NotificationsCard } from "../components/NotificationsCard";
import { PipelineTable } from "../components/PipelineTable";
import { StuckTable } from "../components/StuckTable";
import { ageIntent, formatAge } from "../lib/age";

const LAST_CONFIRM_PICK_WARN_MIN = 30;
const LAST_CONFIRM_PICK_DANGER_MIN = 120;

/** "This block's query failed" — rendered inside the block's own card so a
 * failed block keeps its frame instead of collapsing. */
function BlockErrorBanner({ message }: { message: string }): JSX.Element {
  return (
    <div
      role="alert"
      data-testid="dashboard-block-error"
      className="m-4 rounded-[10px] border border-[var(--intent-danger-fg)] bg-[var(--intent-danger-bg)] p-4 text-sm text-[var(--intent-danger-fg)]"
    >
      {message}
    </div>
  );
}

export function DashboardPage(): JSX.Element {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const now = new Date();

  const fulfillment = useFulfillment();
  const attentionCounts = useAttentionCounts();
  const syncStatus = useSyncStatus();

  // `enabled: canReadNotifications` so the query never fires at all for a
  // user without notifications:read — a 403 must not surface as a banner.
  const canReadNotifications = useCan("notifications:read");
  const notifications = useNotifications({ enabled: canReadNotifications });

  const f = fulfillment.data;
  const attention = attentionCounts.data;

  // formatAge's `t` param is a plain `(key, options?) => string`; react-
  // i18next's overloaded TFunction isn't assignable to it under
  // `exactOptionalPropertyTypes`, so adapt it with a thin wrapper.
  const tAge = (key: string, options?: Record<string, unknown>): string =>
    options === undefined ? t(key) : t(key, options);

  const sinoIntent = ageIntent(
    f?.sino_last_call_at ?? null,
    now,
    LAST_CONFIRM_PICK_WARN_MIN,
    LAST_CONFIRM_PICK_DANGER_MIN,
  );

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title={t("dashboard:title")}
        subtitle={
          <span className="inline-flex items-center gap-2" data-testid="dashboard-meta">
            <LiveDot intent={sinoIntent === "neutral" ? "success" : sinoIntent} />
            <span>
              {t("dashboard:meta.sino_last_call", {
                age: formatAge(f?.sino_last_call_at ?? null, now, tAge),
              })}
              {" · "}
              {t("dashboard:meta.marketplace_sync", {
                age: formatAge(syncStatus.data?.last_synced_at ?? null, now, tAge),
              })}
            </span>
          </span>
        }
        actions={
          // CLAUDE.md's "one Button variant=primary per view" names the shadcn
          // `default` variant — same convention as OrdersPage's Export button.
          <Button variant="default" onClick={() => void navigate({ to: "/export-orders" })}>
            {t("dashboard:btn.export")}
          </Button>
        }
      />

      <div className="space-y-6">
        <section data-testid="dashboard-kpi-block">
          {fulfillment.isError ? (
            <SectionCard title={t("dashboard:kpi.block_title")}>
              <BlockErrorBanner message={t("dashboard:error.block")} />
            </SectionCard>
          ) : (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <KpiCard
                label={t("dashboard:kpi.got_awb")}
                caption={t("dashboard:kpi.got_awb_caption")}
              >
                <Windows
                  h1={f?.got_awb.h1 ?? 0}
                  h24={f?.got_awb.h24 ?? 0}
                  d7={f?.got_awb.d7 ?? 0}
                  nonZeroIntent="success"
                />
              </KpiCard>

              <KpiCard
                label={t("dashboard:kpi.stuck")}
                caption={t("dashboard:kpi.stuck_caption")}
                sub={t("dashboard:kpi.stuck_sub", {
                  total: f?.stuck.total ?? 0,
                  n: f?.stuck.actionable ?? 0,
                })}
                // Joined to a comma string (not a literal array) so the URL
                // stays a plain, readable, validated value — matches the
                // pattern used elsewhere for this same search key (see
                // StuckCauseCards' own "View in Orders" link).
                to={{
                  to: "/orders",
                  search: { status: "picked,packed", awb: "missing", sof: "false" },
                }}
              >
                <Windows
                  h1={f?.stuck.h1 ?? 0}
                  h24={f?.stuck.h24 ?? 0}
                  d7={f?.stuck.d7 ?? 0}
                  nonZeroIntent="danger"
                />
              </KpiCard>

              <KpiCard
                label={t("dashboard:kpi.last_confirm_pick")}
                caption={t("dashboard:kpi.last_confirm_pick_caption")}
                intent={ageIntent(
                  f?.last_confirm_pick_at ?? null,
                  now,
                  LAST_CONFIRM_PICK_WARN_MIN,
                  LAST_CONFIRM_PICK_DANGER_MIN,
                )}
              >
                {formatAge(f?.last_confirm_pick_at ?? null, now, tAge)}
              </KpiCard>

              <KpiCard
                label={t("dashboard:kpi.needs_attention")}
                caption={t("dashboard:kpi.needs_attention_caption")}
                sub={t("dashboard:kpi.by_reason", {
                  oversold: attention?.by_reason.oversold ?? 0,
                  buyer_cancel_requested: attention?.by_reason.buyer_cancel_requested ?? 0,
                })}
                to={{ to: "/orders", search: { attention: "any" } }}
                intent={attentionCounts.isError ? "danger" : "neutral"}
              >
                {attentionCounts.isError ? "—" : (attention?.total ?? 0)}
              </KpiCard>
            </div>
          )}
        </section>

        {/* No page-level error gate: PipelineTable isolates each cell to its
            own query, so a failed source degrades only that cell to "—". */}
        <section data-testid="dashboard-pipeline-block">
          <PipelineTable />
        </section>

        <section data-testid="dashboard-stuck-block">
          {fulfillment.isError ? (
            <SectionCard title={t("dashboard:stuck.title")}>
              <BlockErrorBanner message={t("dashboard:error.block")} />
            </SectionCard>
          ) : (
            <StuckTable />
          )}
        </section>

        {/* Permission check runs BEFORE `isError`: a user who can't read
            gets nothing (not a banner); one who can and whose query failed
            gets the banner, never a silently vanished block. */}
        <section data-testid="dashboard-notifications-block">
          {canReadNotifications &&
            (notifications.isError ? (
              <SectionCard title={t("dashboard:notif.title")}>
                <BlockErrorBanner message={t("dashboard:error.block")} />
              </SectionCard>
            ) : (
              <NotificationsCard />
            ))}
        </section>
      </div>
    </div>
  );
}
